// The Season Pass price a visitor is shown (#4966, ADR 0060), and the switch
// between a market's own currency and US dollars. Every upgrade surface reads
// it here (/pricing, the upgrade modal, the trial wall), so a market's price
// is decided once:
//
//   - the visitor's region is the one signal (utils/visitorRegion.js: the
//     browser's time zone, ?region= for QA), read after mount so a
//     prerendered page's first render matches its markup;
//   - a local price shows only while india_masters is on for this visitor
//     (on for everyone, or on for this signed-in account), and only to
//     someone without a plan (a holder's plan changes in dollars);
//   - the visitor can switch to US dollars and back; the choice lives in
//     this page view only (nothing stored);
//   - each surface that shows a local price counts one view, and each switch
//     is counted, server-side (profile_manager_v2 regional_pricing.py).
//
// The page DISPLAYS; payment_manager_v2 CHARGES, holding a local price until
// the card's issuing country agrees. Checkout is asked for the region in
// `checkoutRegion`.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { DEFAULT } from '../config/regions';
import { resolveVisitorRegion } from '../utils/visitorRegion';
import { markRegionalPricingSeen, markRegionalCurrencySwitch } from '../services/api';
import { windowShape } from '../services/astraLite';

export const REGIONAL_PRICING_FLAG = 'india_masters';
// One-payment passes (#4986, founder 2026-10-21): a new Season Pass is one
// payment for a year and does not renew. The server decides what checkout
// charges (payment_manager_v2 products.ONE_PAYMENT_FLAG, the same name);
// this only changes the words, so a page never says "renews" over a pass
// that does not.
export const ONE_PAYMENT_FLAG = 'one_payment_passes';

/** Pure: the price as sold, one payment (renews false) when the flag says so. */
export function asSold(price, onePayment) {
    return onePayment && price.renews ? Object.freeze({ ...price, renews: false }) : price;
}

// Where a local price is shown; the server's twin is regional_pricing.SURFACES.
export const PRICE_SURFACES = Object.freeze(['pricing', 'upgrade_modal', 'trial_wall']);

/** Pure: the region whose price this visitor is shown. */
export function pricingRegion({ visitorRegion, flagOn, held = false, wantsUsd = false }) {
    if (!visitorRegion || !flagOn || held || wantsUsd || !visitorRegion.pricesLocally()) return DEFAULT;
    return visitorRegion;
}

/**
 * {price, onePayment, region, localOffered, showingLocal, switchCurrency, checkoutRegion}.
 * `price` is {currency, amount, label, renews}; `localOffered` is true when
 * the switch should be shown; `switchCurrency()` flips it.
 */
export default function useSeasonPassPrice({ surface, held = false } = {}) {
    const { currentUser } = useAuth();
    const { publicFlags, flags, forEmail } = useFeatureFlags({ mine: Boolean(currentUser), public: true }) || {};
    const mine = (flag) => Boolean(currentUser) && Boolean(flags?.[flag]) && forEmail === currentUser.email;
    const flagOn = Boolean(publicFlags?.[REGIONAL_PRICING_FLAG]) || mine(REGIONAL_PRICING_FLAG);
    // A holder is never sold a one-payment pass (their plan keeps its own
    // terms), so the words change only for someone without a plan.
    const onePaymentFlag = Boolean(publicFlags?.[ONE_PAYMENT_FLAG]) || mine(ONE_PAYMENT_FLAG);
    const onePayment = !held && onePaymentFlag;
    const [visitor, setVisitor] = useState(null);
    useEffect(() => { setVisitor(resolveVisitorRegion()); }, []);
    const [wantsUsd, setWantsUsd] = useState(false);

    const local = pricingRegion({ visitorRegion: visitor?.region, flagOn, held });
    const localOffered = local !== DEFAULT;
    const region = localOffered && !wantsUsd ? local : DEFAULT;

    const seen = useRef(false);
    useEffect(() => {
        if (!localOffered || seen.current || !PRICE_SURFACES.includes(surface)) return;
        seen.current = true;
        markRegionalPricingSeen({ region: local.code, region_source: visitor.source, surface, ...windowShape() });
    }, [localOffered, local, visitor, surface]);

    const switchCurrency = useCallback(() => {
        if (!localOffered) return;
        const toUsd = !wantsUsd;
        setWantsUsd(toUsd);
        markRegionalCurrencySwitch({ region: local.code, region_source: visitor?.source, surface,
            to: toUsd ? DEFAULT.currency : local.currency, ...windowShape() });
    }, [localOffered, wantsUsd, local, visitor, surface]);

    return {
        price: asSold(region.seasonPass(), onePayment),
        // True while a new Season Pass is sold as one payment (#4986): the
        // Through Graduation offer rides the same flag.
        onePayment,
        // The flag itself, plan held or not: a comped student may still be
        // offered Through Graduation (#5015).
        onePaymentFlag,
        region,
        localOffered,
        showingLocal: region !== DEFAULT,
        switchCurrency,
        // Sent only where a local price was offered (its own, or dollars
        // after the switch); every other checkout's body is unchanged.
        checkoutRegion: localOffered ? region.code : undefined,
    };
}
